import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Loader2Icon } from 'lucide-react'
import {
  ensureInboxCallChatChannel,
  ensureInboxChatChannel,
  fetchChatMessages,
  sendChatMessage,
  uploadChatAttachment,
  markChannelRead,
  chatSenderLabel,
  type ChatMessage,
} from '../../api/teamChat'
import { ChatComposeBar } from '../chat/ChatComposeBar'
import { ChatMessageBody } from '../chat/ChatMessageBody'
import { supabase } from '../../lib/supabase'
import { getErrorMessage } from '../../lib/errors'
import type { InboxItemType } from '../../types/inbox'

interface InboxTeamDiscussPanelProps {
  itemType: InboxItemType
  itemId: string
  onChannelReady?: (channelId: string) => void
}

/** Staff-only team discussion thread for an inbox conversation or call. */
export function InboxTeamDiscussPanel({
  itemType,
  itemId,
  onChannelReady,
}: InboxTeamDiscussPanelProps) {
  const [channelId, setChannelId] = useState<string | null>(null)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [body, setBody] = useState('')
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const listRef = useRef<HTMLDivElement>(null)

  const loadMessages = useCallback(async (cid: string) => {
    const rows = await fetchChatMessages(cid)
    setMessages(rows)
    await markChannelRead(cid)
  }, [])

  const init = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const cid =
        itemType === 'phone_call_log'
          ? await ensureInboxCallChatChannel(itemId)
          : await ensureInboxChatChannel(itemId)
      setChannelId(cid)
      onChannelReady?.(cid)
      await loadMessages(cid)
    } catch (e) {
      setError(getErrorMessage(e, 'Failed to load team discuss'))
      setChannelId(null)
    } finally {
      setLoading(false)
    }
  }, [itemType, itemId, loadMessages, onChannelReady])

  useEffect(() => {
    init()
  }, [init])

  useEffect(() => {
    if (!channelId) return
    const channel = supabase
      .channel(`inbox-team-discuss-${channelId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'chat_messages',
          filter: `channel_id=eq.${channelId}`,
        },
        () => {
          loadMessages(channelId).catch(() => {})
        }
      )
      .subscribe()
    return () => {
      supabase.removeChannel(channel)
    }
  }, [channelId, loadMessages])

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight })
  }, [messages])

  const handleSend = async (file?: File | null, mentionedUserIds?: string[]) => {
    if (!channelId || sending) return
    if (!body.trim() && !file) return
    setSending(true)
    setError(null)
    try {
      let attachmentUrl: string | null = null
      if (file) {
        attachmentUrl = await uploadChatAttachment(channelId, file)
      }
      await sendChatMessage(channelId, body, attachmentUrl, mentionedUserIds)
      setBody('')
      await loadMessages(channelId)
    } catch (e) {
      setError(getErrorMessage(e, 'Failed to send'))
    } finally {
      setSending(false)
    }
  }

  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center p-6">
        <Loader2Icon className="w-5 h-5 animate-spin text-gray-400" />
      </div>
    )
  }

  const channelUnavailable = !channelId && Boolean(error)

  return (
    <div className="flex flex-col flex-1 min-h-0 min-w-0 w-full overflow-hidden">
      <p className="text-[10px] text-gray-500 px-3 pt-2 pb-1 shrink-0 leading-snug">
        Team discuss — only RBC staff see this. The customer never sees these messages.
      </p>
      {error && (
        <div className="mx-2 mb-1 px-2 py-1.5 text-xs text-red-700 bg-red-50 rounded">{error}</div>
      )}
      <div ref={listRef} className="flex-1 overflow-y-auto px-2 pb-2 space-y-2 min-h-0">
        {channelUnavailable ? (
          <p className="text-xs text-gray-400 text-center py-6">Team discuss unavailable for this item</p>
        ) : messages.length === 0 ? (
          <p className="text-xs text-gray-400 text-center py-6">No team messages yet</p>
        ) : (
          messages.map((m) => (
            <div key={m.id} className="bg-gray-50 rounded-lg px-2.5 py-2">
              <div className="flex items-baseline justify-between gap-2 mb-0.5">
                <span className="text-[11px] font-medium text-gray-700">{chatSenderLabel(m)}</span>
                <span className="text-[10px] text-gray-400 flex-shrink-0">
                  {new Date(m.created_at).toLocaleString(undefined, {
                    month: 'short',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                  })}
                </span>
              </div>
              <ChatMessageBody body={m.body} attachmentUrl={m.attachment_url} />
            </div>
          ))
        )}
      </div>
      <div className="border-t border-gray-100 p-2 shrink-0">
        <ChatComposeBar
          value={body}
          onChange={setBody}
          onSend={handleSend}
          disabled={!channelId || channelUnavailable}
          sending={sending}
          placeholder={
            channelUnavailable ? 'Team discuss unavailable' : 'Message the team… Use @ to ping someone'
          }
        />
      </div>
    </div>
  )
}
